import React, { useState, useEffect } from 'react';
import { Table, Select, Space, DatePicker, Row, Col, Card, message, Statistic, Progress, Tabs, Tag, Button } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, DownloadOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { getSalesTeams, getTeamSummary, getPersonSummary, getExpenseStructure, getExpenseTrend, getSampleDetail, getProducts, exportSampleDetail, exportPersonSummary } from '../../../services/api';
import { getAnnualTaskStats } from '../../../services/api';

const { RangePicker } = DatePicker;

export default function SummaryReport() {
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(undefined);
  const [dateRange, setDateRange] = useState([dayjs().startOf('year'), dayjs()]);
  const [summaryData, setSummaryData] = useState([]);
  const [personSummaryData, setPersonSummaryData] = useState([]);
  const [personTeamId, setPersonTeamId] = useState(undefined);
  const [structureData, setStructureData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [personExpenseType, setPersonExpenseType] = useState([]);
  const [sampleData, setSampleData] = useState([]);
  const [sampleStats, setSampleStats] = useState({ totalQty: 0, totalAmount: 0 });
  const [sampleTeamId, setSampleTeamId] = useState(undefined);
  const [sampleProductId, setSampleProductId] = useState(undefined);
  const [sampleProducts, setSampleProducts] = useState([]);

  const [activeTab, setActiveTab] = useState('person');

  const monthNames = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'];
  const quarterNames = ['一季度', '二季度', '三季度', '四季度'];

  // 年度任务数据
  const [annualStats, setAnnualStats] = useState(null);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());

  const tabDisablesDatePicker = ['rank', 'charts'].includes(activeTab);

  const params = {
    from: dateRange[0]?.format('YYYY-MM-DD'),
    to: dateRange[1]?.format('YYYY-MM-DD'),
    teamId,
  };

  const currentYear = dayjs().year();
  const years = [];
  for (let y = currentYear - 2; y <= currentYear + 1; y++) years.push(y);

  const fetchData = async () => {
    try {
      const [tRes, sRes, pRes, stRes, trRes, aRes, smRes, prodRes] = await Promise.all([
        getSalesTeams(),
        getTeamSummary(params),
        getPersonSummary({ ...params, teamId: personTeamId, expenseType: personExpenseType.length > 0 ? personExpenseType.join(',') : undefined }),
        getExpenseStructure(params),
        getExpenseTrend(params),
        getAnnualTaskStats(selectedYear),
        getSampleDetail({ ...params, teamId: sampleTeamId, productId: sampleProductId }),
        getProducts(),
      ]);
      setTeams(tRes.data?.list || []);
      setSummaryData(sRes.data || []);
      setPersonSummaryData(pRes.data || []);
      setStructureData(stRes.data || []);
      setTrendData(trRes.data || []);
      setAnnualStats(aRes.data);
      setSampleData(smRes.data?.list || []);
      setSampleStats({ totalQty: smRes.data?.totalQty || 0, totalAmount: smRes.data?.totalAmount || 0 });
      setSampleProducts(prodRes.data?.list || []);
    } catch (err) { message.error(err.message); }
  };

  useEffect(() => { fetchData(); }, [teamId, personTeamId, dateRange, selectedYear, personExpenseType.join(','), sampleTeamId, sampleProductId]);

  const handleExportSample = async () => {
    try {
      const blob = await exportSampleDetail({ ...params, teamId: sampleTeamId, productId: sampleProductId });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `样品费用明细_${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error(err.message || '导出失败');
    }
  };

  const handleExportPerson = async () => {
    try {
      const blob = await exportPersonSummary({ ...params, teamId: personTeamId, expenseType: personExpenseType.length > 0 ? personExpenseType.join(',') : undefined });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `销售人员明细_${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error(err.message || '导出失败');
    }
  };

  const summaryColumns = [
    { title: '团队', dataIndex: 'teamName' },
    { title: '销售任务(元)', dataIndex: 'taskAmount', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '总业绩(元)', dataIndex: 'salesAmount', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '回款额(元)', dataIndex: 'collectedAmount', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '完成率', dataIndex: 'completionRate', render: (v) => {
      if (!v) return '-';
      return Number(v) > 100
        ? <><Progress percent={100} size="small" strokeColor={Number(v) >= 80 ? '#52c41a' : Number(v) >= 50 ? '#fa8c16' : '#ff4d4f'} /><span style={{ marginLeft: 4 }}>{v}%</span></>
        : <Progress percent={Number(v)} size="small" strokeColor={Number(v) >= 80 ? '#52c41a' : Number(v) >= 50 ? '#fa8c16' : '#ff4d4f'} />;
    } },
    { title: '差旅费', dataIndex: ['expenses', 'travel'], render: (v) => v ? `¥${Number(v).toLocaleString()}` : '-' },
    { title: '招待费', dataIndex: ['expenses', 'entertain'], render: (v) => v ? `¥${Number(v).toLocaleString()}` : '-' },
    { title: '样品费', dataIndex: ['expenses', 'sample'], render: (v) => v ? `¥${Number(v).toLocaleString()}` : '-' },
    { title: '投流费/佣金', dataIndex: ['expenses', 'traffic'], render: (v) => v ? `¥${Number(v).toLocaleString()}` : '-' },
    { title: '费用合计', dataIndex: 'expenseTotal', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '费用占比', dataIndex: 'expenseRate', render: (v) => v ? `${v}%` : '-' },
  ];

  // 团队年度排名
  const rankColumns = [
    { title: '排名', render: (_, r, i) => i + 1 },
    { title: '团队', dataIndex: 'teamName' },
    { title: '年度任务额', dataIndex: 'taskAmount', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '已完成', dataIndex: 'completed', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '完成率', dataIndex: 'rate', render: (v) => {
      const color = Number(v) >= 80 ? '#52c41a' : Number(v) >= 50 ? '#fa8c16' : '#ff4d4f';
      return Number(v) > 100 ? <><Progress percent={100} size="small" strokeColor={color} /><span style={{ marginLeft: 4 }}>{v}%</span></> : <Progress percent={Number(v)} size="small" strokeColor={color} />;
    } },
    { title: '费用', dataIndex: 'expense', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '费用占比', dataIndex: 'expenseRatio', render: (v) => v ? `${v}%` : '-' },
  ];

  const personColumns = [
    { title: '姓名', dataIndex: 'name' },
    { title: '团队', dataIndex: 'teamName' },
    { title: '销售任务(元)', dataIndex: 'taskAmount', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '总业绩(元)', dataIndex: 'salesAmount', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '回款额(元)', dataIndex: 'collectedAmount', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '完成率', dataIndex: 'completionRate', render: (v) => {
      if (!v) return '-';
      return Number(v) > 100
        ? <><Progress percent={100} size="small" strokeColor={Number(v) >= 80 ? '#52c41a' : Number(v) >= 50 ? '#fa8c16' : '#ff4d4f'} /><span style={{ marginLeft: 4 }}>{v}%</span></>
        : <Progress percent={Number(v)} size="small" strokeColor={Number(v) >= 80 ? '#52c41a' : Number(v) >= 50 ? '#fa8c16' : '#ff4d4f'} />;
    } },
    { title: '差旅费', dataIndex: ['expenses', 'travel'], render: (v) => v ? `¥${Number(v).toLocaleString()}` : '-' },
    { title: '招待费', dataIndex: ['expenses', 'entertain'], render: (v) => v ? `¥${Number(v).toLocaleString()}` : '-' },
    { title: '样品费', dataIndex: ['expenses', 'sample'], render: (v) => v ? `¥${Number(v).toLocaleString()}` : '-' },
    { title: '投流费/佣金', dataIndex: ['expenses', 'traffic'], render: (v) => v ? `¥${Number(v).toLocaleString()}` : '-' },
    { title: '费用合计', dataIndex: 'expenseTotal', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '费用占比', dataIndex: 'expenseRate', render: (v) => v ? `${v}%` : '-' },
  ];

  const computeRowSpan = (data, key) => {
    const spans = new Array(data.length).fill(0);
    let i = 0;
    while (i < data.length) {
      if (!data[i][key]) { spans[i] = 0; i++; continue; }
      let count = 1;
      for (let j = i + 1; j < data.length; j++) {
        if (!data[j][key]) count++; else break;
      }
      spans[i] = count;
      i += count;
    }
    return spans;
  };

  const teamSpans = computeRowSpan(sampleData, 'teamName');
  const personSpans = computeRowSpan(sampleData, 'personName');

  const sampleColumns = [
    { title: '团队', dataIndex: 'teamName', onCell: (_, index) => ({ rowSpan: teamSpans[index] }) },
    { title: '人员', dataIndex: 'personName', onCell: (_, index) => ({ rowSpan: personSpans[index] }) },
    { title: '样品', dataIndex: 'productLabel' },
    { title: '数量', render: (_, r) => `${r.qty}（${r.unit}）` },
    { title: '单价', render: (_, r) => `¥${r.unitPrice}` },
    { title: '合计', render: (_, r) => `¥${Number(r.subtotal).toLocaleString()}` },
    { title: '个人总计', render: (_, r) => r.personTotal ? `¥${Number(r.personTotal).toLocaleString()}` : '' },
  ];

  const quarterChartOption = annualStats?.quarters ? {
    title: { text: '各季度销售额完成情况', left: 'center' },
    tooltip: { trigger: 'axis' },
    legend: { data: ['销售任务', '销售额', '回款'], bottom: 0 },
    xAxis: { type: 'category', data: annualStats.quarters.map(q => `Q${q.quarter}`) },
    yAxis: { type: 'value', name: '金额(元)' },
    series: [
      {
        name: '销售任务', type: 'bar', data: annualStats.quarters.map(q => q.quarterAmount),
        itemStyle: { color: '#1890ff' },
        label: { show: true, position: 'top', formatter: p => p.value >= 10000 ? (p.value / 10000).toFixed(1) + 'w' : p.value },
      },
      {
        name: '销售额', type: 'bar', data: annualStats.quarters.map(q => q.completed),
        itemStyle: { color: '#52c41a' },
        label: { show: true, position: 'top', formatter: p => p.value >= 10000 ? (p.value / 10000).toFixed(1) + 'w' : p.value },
      },
      {
        name: '回款', type: 'bar', data: annualStats.quarters.map(q => q.collected || 0),
        itemStyle: { color: '#fa8c16' },
        label: { show: true, position: 'top', formatter: p => p.value >= 10000 ? (p.value / 10000).toFixed(1) + 'w' : p.value },
      },
    ],
  } : {};

  const pieOption = {
    title: { text: '销售费用结构', left: 'center' },
    tooltip: { trigger: 'item', formatter: '{b}: ¥{c} ({d}%)' },
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      data: structureData,
      label: { formatter: '{b}\n¥{c}' },
    }],
  };

  const trendOption = {
    title: { text: '销售费用占比趋势', left: 'center' },
    tooltip: { trigger: 'axis' },
    legend: { data: ['销售额', '费用合计', '费用占比'], bottom: 0 },
    xAxis: { type: 'category', data: trendData.map(d => dayjs(d.month).format('YYYY-MM')) },
    yAxis: [
      { type: 'value', name: '金额(元)' },
      { type: 'value', name: '占比(%)', axisLabel: { formatter: '{value}%' } },
    ],
    series: [
      { name: '销售额', type: 'bar', data: trendData.map(d => d.salesAmount) ,label:{show:true,position: 'top'}},
      { name: '费用合计', type: 'bar', data: trendData.map(d => d.expenseTotal),label:{show:true,position: 'top'} },
      { name: '费用占比', type: 'line', yAxisIndex: 1, data: trendData.map(d => d.expenseRate || 0), label: { show: true, position: 'top', formatter: '{c}%' } },
    ],
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        请选择日期：<RangePicker picker="month" value={dateRange} disabled={tabDisablesDatePicker} onChange={(dates) => setDateRange(dates || [dayjs().startOf('year'), dayjs()])} />
      </Space>

      {/* 年度完成率卡片 */}
      {annualStats && annualStats.annualAmount > 0 && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={12} lg={6}>
            <Card size="small">
              <Statistic title="年度任务总额"
                value={annualStats.annualAmount}
                formatter={(v) => `¥${Number(v).toLocaleString()}`}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={12} lg={6}>
            <Card size="small">
              <Statistic title="年度已完成"
                value={annualStats.completed}
                formatter={(v) => `¥${Number(v).toLocaleString()}`}
                valueStyle={{ color: Number(annualStats.rate) >= 100 ? '#52c41a' : '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={12} lg={6}>
            <Card size="small">
              <Statistic title="年度完成率"
                value={annualStats.rate}
                suffix="%"
                prefix={Number(annualStats.rate) >= 100 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                valueStyle={{ color: Number(annualStats.rate) >= 100 ? '#52c41a' : '#faad14' }}
              />
              <Progress percent={Number(annualStats.rate)} strokeColor={Number(annualStats.rate) >= 80 ? '#52c41a' : Number(annualStats.rate) >= 50 ? '#fa8c16' : '#ff4d4f'} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={12} lg={6}>
            <Card size="small">
              <Statistic title="各季度状态" valueRender={() => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{(annualStats.quarters || []).map(q => (
                  <Tag key={q.quarter} color={Number(q.rate) >= 100 ? 'green' : Number(q.rate) >= 50 ? 'orange' : 'red'}>
                    Q{q.quarter}: {q.rate}%
                  </Tag>
                ))}</div>
              )} />
            </Card>
          </Col>
        </Row>
      )}

      <Tabs activeKey={activeTab} onChange={(key) => { setActiveTab(key); }} items={[
        {
          key: 'person', label: '销售人员汇总',
          children: (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <Select allowClear placeholder="请选择团队" style={{ width: 180 }}
                          options={teams.map(t => ({ label: t.name, value: t.id }))}
                          value={personTeamId}
                          onChange={(v) => setPersonTeamId(v)} />
                  <Select mode="multiple" allowClear placeholder="费用类型（多选）" style={{ width: 260, marginLeft: 8 }}
                          options={[
                            { label: '差旅费', value: 'travel' },
                            { label: '招待费', value: 'entertain' },
                            { label: '样品费', value: 'sample' },
                            { label: '投流费/佣金', value: 'traffic' },
                          ]}
                          value={personExpenseType}
                          onChange={(v) => setPersonExpenseType(v)} />
                  <Button type="primary" icon={<DownloadOutlined />} style={{ marginLeft: 8 }} onClick={handleExportPerson}>导出 Excel</Button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <Table dataSource={personSummaryData} columns={personColumns} rowKey="salespersonId" pagination={false}
                         scroll={{ x: 'max-content' }}
                         summary={() => {
                           if (!personSummaryData.length) return null;
                           const total = data => data.reduce((s, r) => s + (Number(r) || 0), 0);
                           const task = total(personSummaryData.map(r => r.taskAmount));
                           const sales = total(personSummaryData.map(r => r.salesAmount));
                           const collected = total(personSummaryData.map(r => r.collectedAmount));
                           const travel = total(personSummaryData.map(r => r.expenses?.travel));
                           const entertain = total(personSummaryData.map(r => r.expenses?.entertain));
                           const sample = total(personSummaryData.map(r => r.expenses?.sample));
                           const traffic = total(personSummaryData.map(r => r.expenses?.traffic));
                           const expense = total(personSummaryData.map(r => r.expenseTotal));
                           const rate = sales && task ? ((sales / task) * 100).toFixed(2) : null;
                           const expenseRate = sales ? ((expense / sales) * 100).toFixed(2) : null;
                           return (
                             <Table.Summary fixed>
                               <Table.Summary.Row style={{ color: '#1890ff' }}>
                                 <Table.Summary.Cell/>
                                 <Table.Summary.Cell><strong>团队合计：</strong></Table.Summary.Cell>
                                 <Table.Summary.Cell><strong>¥{task.toLocaleString()}</strong></Table.Summary.Cell>
                                 <Table.Summary.Cell><strong>¥{sales.toLocaleString()}</strong></Table.Summary.Cell>
                                 <Table.Summary.Cell><strong>¥{collected.toLocaleString()}</strong></Table.Summary.Cell>
                                 <Table.Summary.Cell><strong>{rate ? `${rate}%` : '-'}</strong></Table.Summary.Cell>
                                 <Table.Summary.Cell><strong>¥{travel.toLocaleString()}</strong></Table.Summary.Cell>
                                 <Table.Summary.Cell><strong>¥{entertain.toLocaleString()}</strong></Table.Summary.Cell>
                                 <Table.Summary.Cell><strong>¥{sample.toLocaleString()}</strong></Table.Summary.Cell>
                                 <Table.Summary.Cell><strong>¥{traffic.toLocaleString()}</strong></Table.Summary.Cell>
                                 <Table.Summary.Cell><strong>¥{expense.toLocaleString()}</strong></Table.Summary.Cell>
                                 <Table.Summary.Cell><strong>{expenseRate ? `${expenseRate}%` : '-'}</strong></Table.Summary.Cell>
                               </Table.Summary.Row>
                             </Table.Summary>
                           );
                         }} />
                </div>
              </div>
          ),
        },
        {
          key: 'summary', label: '团队汇总',
          children: (
              <div style={{ overflowX: 'auto' }}>
                <Table dataSource={summaryData} columns={summaryColumns} rowKey="teamId" pagination={false}
                       scroll={{ x: 'max-content' }} />
              </div>
          ),
        },
        {
          key: 'sample', label: '样品费用明细',
          children: (
            <div>
              <div style={{ marginBottom: 12 }}>
                <Select allowClear placeholder="请选择团队" style={{ width: 180 }}
                        options={teams.map(t => ({ label: t.name, value: t.id }))}
                        value={sampleTeamId}
                        onChange={(v) => setSampleTeamId(v)} />
                <Select allowClear placeholder="请选择样品" style={{ width: 260, marginLeft: 8 }}
                        options={sampleProducts.map(p => ({ label: `${p.name}${p.spec ? ` ${p.spec}` : ''}`, value: p.id }))}
                        value={sampleProductId}
                        onChange={(v) => setSampleProductId(v)}
                        showSearch
                        filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())} />
                <Button type="primary" icon={<DownloadOutlined />} style={{ marginLeft: 8 }} onClick={handleExportSample}>导出 Excel</Button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <Table dataSource={sampleData} columns={sampleColumns} rowKey={(_, i) => i} pagination={false}
                       scroll={{ x: 'max-content' }}
                       summary={() => (
                         <Table.Summary fixed>
                           <Table.Summary.Row style={{ color: '#1890ff' }}>
                             <Table.Summary.Cell colSpan={3} align="right"><strong>总数量：</strong></Table.Summary.Cell>
                             <Table.Summary.Cell><strong>{sampleStats.totalQty}</strong></Table.Summary.Cell>
                             <Table.Summary.Cell colSpan={1} align="right"><strong>总金额：</strong></Table.Summary.Cell>
                             <Table.Summary.Cell><strong>¥{Number(sampleStats.totalAmount).toLocaleString()}</strong></Table.Summary.Cell>
                             <Table.Summary.Cell />
                           </Table.Summary.Row>
                         </Table.Summary>
                       )} />
              </div>
            </div>
          ),
        },
        {
          key: 'rank', label: '年度团队排名',            children: (
              <div style={{ overflowX: 'auto' }}>
                {annualStats?.teamRank?.length > 0
                  ? <Table dataSource={annualStats.teamRank} columns={rankColumns} rowKey="teamId" pagination={false}
                      scroll={{ x: 'max-content' }} />
                  : <Card><div style={{ textAlign: 'center', color: '#999', padding: 40 }}>暂无年度任务数据</div></Card>}
              </div>
            ),
          },
          {
            key: 'charts', label: '图表',
            children: (
              <div style={{ overflowX: 'auto' }}>
                <Row gutter={[12, 12]}>
                  <Col xs={24} lg={12}>
                    <Card size="small"><ReactECharts option={quarterChartOption} style={{ height: 300 }} /></Card>
                    {annualStats?.quarters && (
                      <Card size="small" title="每月销售完成情况" style={{ marginTop: 12 }}>
                        <Table dataSource={(() => {
                          const rows = [];
                          annualStats.quarters.forEach(q => {
                            (q.months || []).forEach((m, i) => {
                              const monthNum = (q.quarter - 1) * 3 + i + 1;
                              rows.push({ ...m, quarter: quarterNames[q.quarter - 1], monthLabel: monthNames[monthNum - 1], _firstInQuarter: i === 0, _lastInQuarter: i === 2 });
                            });
                          });
                          return rows;
                        })()}
                        columns={[
                          { title: '季度', dataIndex: 'quarter', width: 80, render: (v, r) => r._firstInQuarter ? v : '' },
                          { title: '月份', dataIndex: 'monthLabel' },
                          { title: '销售任务(元)', dataIndex: 'taskAmount', render: (v) => `¥${Number(v).toLocaleString()}` },
                          { title: '销售额(元)', dataIndex: 'completed', render: (v) => `¥${Number(v).toLocaleString()}` },
                          { title: '完成率', dataIndex: 'rate', render: (v) => {
                            const color = Number(v) >= 80 ? '#52c41a' : Number(v) >= 50 ? '#fa8c16' : '#ff4d4f';
                            return Number(v) > 100 ? <><Progress percent={100} size="small" strokeColor={color} /><span style={{ marginLeft: 4 }}>{v}%</span></> : <Progress percent={Number(v)} size="small" strokeColor={color} />;
                          } },
                        ]}
                        rowKey={(r) => r.month + '-' + r.quarter} pagination={false} size="small" />
                      </Card>
                    )}
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card size="small"><ReactECharts option={pieOption} style={{ height: 300 }} /></Card>
                    <Card size="small" style={{ marginTop: 12 }}><ReactECharts option={trendOption} style={{ height: 300 }} /></Card>
                  </Col>
                </Row>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
